import StaffHistory from "./StaffHistory";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, Circle, BookOpen, Landmark } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { simulate, type DebtItem, type Method } from "@/lib/metrics/payoff";
import { SEATS, normalizeSeat } from "@/lib/desk/career";
import { formatGateCurrent, type GateStatus } from "@/hooks/useGateStatus";
import { cn } from "@/lib/utils";
import { currentSnapshot } from "@/lib/metrics/accounts";

const db = supabase as any;
type Entry = { metric: string; value: number; recorded_at: string; created_at?: string; details?: any };
type Tile = "savings" | "debt" | "fat" | "weight";
const money = (n: number) => `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;
const SHADES = ["hsl(var(--gold-dark, var(--gold)))", "hsl(var(--gold))", "hsl(var(--gold) / 0.6)", "hsl(var(--gold) / 0.35)", "hsl(var(--gold) / 0.2)"];
const SEAT_ORDER = ["intern", "analyst", "trader", "senior_trader"] as const;
const fmtDay = (d: string) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Donut({ parts, label }: { parts: number[]; label: string }) {
  const total = parts.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  return (
    <div className="relative mx-auto h-32 w-32">
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
        {parts.map((p, i) => {
          const len = (p / total) * 100, off = acc; acc += len;
          return <circle key={i} cx="18" cy="18" r="15.9" fill="none" stroke={SHADES[i % SHADES.length]} strokeWidth="4" strokeDasharray={`${len} ${100 - len}`} strokeDashoffset={-off} />;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="font-display text-xl">{label}</span><span className="text-[10px] text-muted-foreground">Total</span></div>
    </div>
  );
}

function LineChart({ pts }: { pts: number[] }) {
  if (pts.length < 2) return <p className="py-6 text-center text-xs text-muted-foreground">Needs two entries to draw a line.</p>;
  const max = Math.max(...pts), min = Math.min(...pts), r = max - min || 1;
  const d = pts.map((p, i) => `${(i / (pts.length - 1)) * 100},${36 - ((p - min) / r) * 32}`).join(" ");
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-24 w-full">
      {[4, 20, 36].map((y) => <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="hsl(var(--gold) / 0.15)" strokeWidth="0.4" />)}
      <polyline points={d} fill="none" stroke="hsl(var(--gold))" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const Card = ({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) => (
  <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3">
    <div className="mb-2 flex items-baseline justify-between"><h3 className="font-display text-base">{title}</h3>{right && <span className="text-xs text-muted-foreground">{right}</span>}</div>
    {children}
  </section>
);

export function FullRecord({ memberId, entries, gate, stage, book, income }: {
  memberId: string; entries: Entry[]; gate: GateStatus | null; stage: number; book: any[] | null;
  income: { annual: number | null; reliableMonthly?: number | null; stability?: string | null };
}) {
  const [tile, setTile] = useState<Tile>("debt");
  const { data: sim } = useQuery({
    queryKey: ["coach-sim", memberId],
    queryFn: async () => {
      const [seat, acct, lessons] = await Promise.all([
        db.from("desk_progression").select("seat").eq("user_id", memberId).eq("track_id", "trade").maybeSingle(),
        db.from("sim_accounts").select("cash, realized_pnl").eq("user_id", memberId).eq("kind", "personal").maybeSingle(),
        db.from("intern_academy_progress").select("lesson_id", { count: "exact", head: true }).eq("user_id", memberId),
      ]);
      return { seat: seat.data?.seat ?? null, cash: acct.data?.cash ?? null, lessons: lessons.count ?? 0 };
    },
  });

  const by = (m: string) => entries.filter((e) => e.metric === m);
  const sav = by("savings"), debt = by("debt"), fit = by("fitness");
  const currentSavings = currentSnapshot(sav), currentDebt = currentSnapshot(debt);
  const last = <T,>(a: T[]) => a[a.length - 1];
  const weights = fit.map((e) => Number(e.details?.weight)).filter((n) => n > 0);
  const fats = fit.map((e) => Number(e.value));
  const tiles: { k: Tile; name: string; val: string; delta: number | null; fmt: (n: number) => string; goodUp: boolean }[] = [
    { k: "savings", name: "Savings", val: currentSavings ? money(currentSavings.value) : "—", delta: sav.length > 1 && currentSavings ? currentSavings.value - sav[0].value : null, fmt: money, goodUp: true },
    { k: "debt", name: "Debt", val: currentDebt ? money(currentDebt.value) : "—", delta: debt.length > 1 && currentDebt ? currentDebt.value - debt[0].value : null, fmt: money, goodUp: false },
    { k: "fat", name: "Body fat", val: fats.length ? `${last(fats).toFixed(1)}%` : "—", delta: fats.length > 1 ? last(fats) - fats[0] : null, fmt: (n) => n.toFixed(1), goodUp: false },
    { k: "weight", name: "Weight", val: weights.length ? `${Math.round(last(weights))} lb` : "—", delta: weights.length > 1 ? last(weights) - weights[0] : null, fmt: (n) => String(Math.round(n)), goodUp: false },
  ];

  const detail = () => {
    if (tile === "debt") {
      const d = currentDebt?.details ?? {};
      const items: DebtItem[] = (d.debts ?? []).filter((x: DebtItem) => x.balance > 0);
      const total = items.reduce((s, x) => s + x.balance, 0);
      if (!items.length) return <Empty text="No cards or loans listed yet." />;
      const method = (d.method ?? "snowball") as Method;
      let free: string | null = null;
      try { const r = simulate(items, Number(d.extra) || 0, method, d.liabilities ?? [], d.sim_opts ?? {}); if (!r.stalled) { const t = new Date(); t.setMonth(t.getMonth() + r.months); free = t.toLocaleDateString(undefined, { month: "short", year: "numeric" }); } } catch { /* plan not computable */ }
      return (
        <>
          <Donut parts={items.map((x) => x.balance)} label={money(total)} />
          <ul className="mt-3 space-y-2">
            {items.map((x, i) => (
              <li key={x.id} className="flex items-start gap-2 text-xs">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: SHADES[i % SHADES.length] }} />
                <div className="flex-1"><p className="font-medium text-foreground">{x.name || "Debt"}</p><p className="text-muted-foreground">{money(x.balance)} · {x.apr}% APR · min {money(x.min)}</p></div>
                <span className="text-muted-foreground">{Math.round((x.balance / total) * 100)}%</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 border-t border-amber-500/20 pt-2 text-xs text-muted-foreground">Plan: <span className="capitalize">{method}</span>{free ? ` · debt-free ${free}` : ""}</p>
        </>
      );
    }
    if (tile === "savings") {
      const d = currentSavings?.details ?? {};
      const parts = (d.financial_accounts_version === 2 ? ((d.accounts ?? []) as any[]).map((a) => ({n:a.name,v:a.balance})) : [
        { n: "Cash / savings", v: (d.trad_cash ?? 0) }, { n: "Money market", v: (d.trad_mm ?? 0) + (d.ret_mm ?? 0) },
        { n: "Stocks", v: (d.trad_stocks ?? 0) + (d.ret_stocks ?? 0) }, { n: "Retirement", v: (d.ret_cash ?? 0) + (d.ret_other ?? 0) },
        { n: "Other", v: d.trad_other ?? 0 }, ...((d.accounts ?? []) as any[]).map((a) => ({ n: a.name, v: a.balance })),
      ]).filter((p) => p.v > 0);
      if (!parts.length) return <Empty text="No savings accounts listed yet." />;
      const tot = parts.reduce((s, p) => s + p.v, 0);
      return (
        <>
          <Donut parts={parts.map((p) => p.v)} label={money(tot)} />
          {tiles[0].delta != null && <p className="mt-2 text-center text-sm text-emerald-700">{tiles[0].delta >= 0 ? "+" : ""}{money(tiles[0].delta)} since start</p>}
          <ul className="mt-3 space-y-2">{parts.map((p, i) => (
            <li key={p.n + i} className="flex items-center gap-2 text-xs"><span className="h-2.5 w-2.5 rounded-full" style={{ background: SHADES[i % SHADES.length] }} /><span className="flex-1">{p.n} · {money(p.v)}</span><span className="text-muted-foreground">{Math.round((p.v / tot) * 100)}%</span></li>
          ))}</ul>
        </>
      );
    }
    const isFat = tile === "fat";
    const pts = isFat ? fats : weights;
    if (!pts.length) return <Empty text={isFat ? "No body fat logged yet." : "No weight logged yet."} />;
    const f = (n: number) => (isFat ? `${n.toFixed(1)}%` : `${Math.round(n)} lb`);
    const lf = last(fit);
    return (
      <>
        <div className="flex items-center justify-around text-center">
          <div><p className="font-display text-xl">{f(pts[0])}</p><p className="text-[10px] text-muted-foreground">Start {fit[0] && `· ${fmtDay(fit[0].recorded_at)}`}</p></div>
          <span className="text-gold">→</span>
          <div><p className="font-display text-xl">{f(last(pts))}</p><p className="text-[10px] text-muted-foreground">Latest</p></div>
        </div>
        <LineChart pts={pts} />
        <p className="text-xs text-muted-foreground">
          {isFat ? (lf?.details?.waist ? `Waist ${lf.details.waist} in · ` : "") : (lf?.details?.height ? `Height ${lf.details.height} · ` : "")}
          Last logged {lf ? fmtDay(lf.recorded_at) : "—"}
        </p>
      </>
    );
  };

  const items = gate?.items ?? [];
  const met = items.filter((i) => i.met).length;
  const seat = sim?.seat ? normalizeSeat(sim.seat) : null;
  const seatIdx = seat ? SEAT_ORDER.indexOf(seat as any) : -1;

  return (
    <div className="space-y-3">
      <div className="text-center"><h2 className="font-display text-2xl">Full record</h2><div className="mx-auto mt-1 h-px w-16 bg-gold" /></div>
      <div className="grid grid-cols-4 gap-1.5">
        {tiles.map((t) => {
          const good = t.delta == null || t.delta === 0 ? null : (t.delta > 0) === t.goodUp;
          const I = (t.delta ?? 0) > 0 ? ArrowUp : ArrowDown;
          return (
            <button key={t.k} onClick={() => setTile(t.k)} aria-pressed={tile === t.k}
              className={cn("relative rounded-lg border bg-card p-1.5 text-center transition", tile === t.k ? "border-2 border-gold shadow-md" : "border-amber-500/25")}>
              <p className="text-[10px] text-muted-foreground">{t.name}</p>
              <p className="truncate font-display text-sm">{t.val}</p>
              {t.delta != null && t.delta !== 0 && <p className={cn("flex items-center justify-center text-[10px]", good ? "text-emerald-700" : "text-destructive")}><I className="h-3 w-3" />{t.fmt(Math.abs(t.delta))}</p>}
              {tile === t.k && <span className="absolute -bottom-[7px] left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b-2 border-r-2 border-gold bg-card" />}
            </button>
          );
        })}
      </div>
      <Card title={{ savings: "Savings breakdown", debt: "Debt breakdown", fat: "Body fat", weight: "Weight" }[tile]}>{detail()}</Card>

      <Card title="Simulation">
        <div className="grid grid-cols-4 gap-1 text-center">
          {SEAT_ORDER.map((s, i) => (
            <div key={s}>
              <div className={cn("mx-auto flex h-11 w-11 items-center justify-center rounded-full border-2", i <= seatIdx ? "border-gold bg-gold text-primary-foreground" : "border-gold/40 bg-gold/10 text-gold/60")}><Landmark className="h-5 w-5" /></div>
              <p className="mt-1 text-[10px]">{SEATS[s]?.title ?? s}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 border-t border-amber-500/20 pt-2 text-center text-xs text-muted-foreground">
          Seat: {seat ? SEATS[seat]?.title : "Not started"} · Academy {sim?.lessons ?? 0} lessons{sim?.cash != null ? ` · ${Math.round(sim.cash).toLocaleString()} coins` : ""}
        </p>
      </Card>

      <Card title="Income">
        {income.annual == null ? <p className="text-xs text-muted-foreground">No income logged yet.</p> :
          <p className="text-sm">{money(income.annual)}/yr · reliable {money(income.reliableMonthly ?? 0)}/mo{income.stability ? ` · ${income.stability}` : ""}</p>}
      </Card>

      <Card title={`Path to Stage ${Math.min(8, stage + 1)}`} right={items.length ? `${met} of ${items.length} done` : undefined}>
        {items.length > 0 && <div className="mb-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gold" style={{ width: `${(met / items.length) * 100}%` }} /></div>}
        <div className="space-y-1.5">
          {items.map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-xs">
              {i.met ? <Check className="h-4 w-4 rounded-full bg-gold p-0.5 text-primary-foreground" /> : <Circle className="h-4 w-4 text-gold/60" />}
              <span className={i.met ? "" : "text-foreground/70"}>{i.label}</span>
              <span className="ml-auto text-muted-foreground">{formatGateCurrent(i)}</span>
            </div>
          ))}
          {!items.length && <p className="text-xs text-muted-foreground">No checklist items for the next stage.</p>}
        </div>
      </Card>

      <Card title="The Book" right={`${book?.length ?? 0} of 12 chapters`}>
        <div className="h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gold" style={{ width: `${((book?.length ?? 0) / 12) * 100}%` }} /></div>
        {!!book?.length && (
          <div className="mt-2 space-y-1.5">
            {book.map((b: any) => (
              <details key={b.chapter} className="rounded-lg border border-amber-500/20 p-2 text-xs">
                <summary className="flex cursor-pointer items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-gold" />Chapter {b.chapter}{b.ai_score != null && <span className="ml-auto text-muted-foreground">score {b.ai_score}</span>}</summary>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-foreground/80">{typeof b.answers === "object" ? Object.values(b.answers ?? {}).join("\n\n") : String(b.answers)}</pre>
                {b.ai_feedback && <p className="mt-2 rounded bg-gold/10 p-2">{b.ai_feedback}</p>}
              </details>
            ))}
          </div>
        )}
      </Card>
      <StaffHistory memberId={memberId} />
    </div>
  );
}

const Empty = ({ text }: { text: string }) => <p className="py-6 text-center text-xs text-muted-foreground">{text}</p>;
